/**
 * Shared utilities for recipe source display (favicon, display name) and video URL detection.
 * Used by both RecipeHeader (detail page) and RecipeBrowserCard (browse grid).
 */

import { parseIngredientText } from './ingredientParser';

export type VideoPlatform = 'instagram' | 'facebook' | 'youtube' | 'tiktok';

export const detectVideoPlatform = (url: string): VideoPlatform | null => {
  const urlLower = url.toLowerCase();
  if (urlLower.includes('instagram.com') || urlLower.includes('instagr.am')) return 'instagram';
  if (urlLower.includes('facebook.com') || urlLower.includes('fb.com') || urlLower.includes('fb.watch')) return 'facebook';
  if (urlLower.includes('tiktok.com') || urlLower.includes('tiktok')) return 'tiktok';
  if (urlLower.includes('youtube.com') || urlLower.includes('youtu.be')) return 'youtube';
  return null;
};

export const isVideoUrl = (url: string): boolean => detectVideoPlatform(url) !== null;

/**
 * Hosts and shapes that mark a URL as unusable as a durable source address.
 *
 * CLIENT TWIN of `isUnstableSourceUrl` in api/_lib/normalizeRecipeUrl.ts — the
 * two MUST stay in sync (there is a parity test in
 * src/utils/__tests__/recipeSource.test.ts). `src/` never imports from `api/`,
 * which is why this is a copy rather than a shared module; the same convention
 * already applies to CANONICAL_DISH_TYPES in src/utils/dishTypeVocabulary.ts.
 */
const REDIRECT_HOSTS = new Set(['nl.nytimes.com', 'p.liadm.com', 't.co', 'bit.ly', 'lnkd.in']);
const REDIRECT_HOST_PREFIXES = ['link.', 'links.', 'click.', 'clicks.', 'trk.', 'track.', 'email.'];

/**
 * True when a recipe's `sourceUrl` cannot be relied on to reach the recipe
 * again. Two cases, both from #575:
 *
 *  1. A newsletter click-tracking wrapper (`nl.nytimes.com/f/a/<token>`). The
 *     token is unique per email send and rotates, so the address works today
 *     and resolves to an ad tracker later — the recipe can never be refreshed.
 *  2. A truncated address whose path ends in a bare `-` (`/recipes/1026425-`),
 *     a string-truncation artefact at import that 404s.
 *
 * A missing URL is NOT unstable — that is a separate, already-tracked issue.
 */
export const isUnstableSourceUrl = (url: string | undefined | null): boolean => {
  if (!url) return false;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return true;
  }

  const host = parsed.hostname.toLowerCase();
  if (REDIRECT_HOSTS.has(host)) return true;
  if (REDIRECT_HOST_PREFIXES.some((prefix) => host.startsWith(prefix))) return true;
  if (/^\/f\/a\//.test(parsed.pathname)) return true;
  if (parsed.pathname.length > 1 && parsed.pathname.endsWith('-')) return true;

  return false;
};

/**
 * True when a recipe has the core CONTENT needed to render usefully: at least
 * one ingredient AND at least one instruction step. Metadata (cook time, image,
 * rating) is deliberately NOT considered — a recipe with full ingredients and
 * steps is renderable and must never trigger a live re-scrape just to backfill
 * metadata. This is the gate that stops the "recette vide" re-scrape storm
 * (#381/#391/#392).
 */
export const hasRecipeContent = (recipe: {
  extendedIngredients?: unknown[];
  instructions?: unknown[];
} | null | undefined): boolean => {
  if (!recipe) return false;
  return (
    (recipe.extendedIngredients?.length ?? 0) > 0 &&
    (recipe.instructions?.length ?? 0) > 0
  );
};

/**
 * True when at least one ingredient has a parseable quantity. Recipes imported
 * from old catalog snapshots can have ingredient names only (no amounts), which
 * makes `hasRecipeContent` return true but leaves the quantity column blank.
 * This gate drives a one-time healing re-scrape to fetch proper ingredient text.
 */
export const hasIngredientQuantities = (recipe: {
  extendedIngredients?: Array<{ original?: string; name?: string }>;
} | null | undefined): boolean => {
  const ings = recipe?.extendedIngredients;
  if (!ings?.length) return false;
  return ings.some(ing => {
    const { numPart } = parseIngredientText(ing.original || ing.name || '');
    return !!numPart;
  });
};

/**
 * True when a recipe carries a REAL description — non-empty and not merely the
 * title echoed back. Imported base-catalog recipes were stored with
 * `description = title` as a placeholder (Firestore `recipes/imported-…` docs),
 * which `convertToRecipe` surfaces as `summary`. That placeholder must not count
 * as a description: it would render the title twice AND suppress the scrape that
 * fetches the real intro paragraph.
 */
export const hasRealDescription = (recipe: {
  summary?: string;
  title?: string;
} | null | undefined): boolean => {
  const summary = (recipe?.summary ?? '').trim();
  if (!summary) return false;
  const title = (recipe?.title ?? '').trim();
  return summary.toLowerCase() !== title.toLowerCase();
};

/**
 * Pick the summary to keep when merging a base/local recipe with its Firestore
 * copy (or when persisting an import). Prefers a REAL description over the title
 * placeholder: keeps the local summary when it's real, otherwise adopts the
 * Firestore/candidate summary when THAT one is real, and falls back to whatever
 * local value we already had when neither is real (a placeholder renders as
 * nothing anyway). Fixes recipes whose real intro paragraph lived only in
 * Firestore while the local/nav snapshot carried an empty or title-echo summary.
 */
export const preferRealSummary = (
  localSummary: string | undefined,
  candidateSummary: string | undefined,
  title: string | undefined,
): string | undefined => {
  if (hasRealDescription({ summary: localSummary, title })) return localSummary;
  if (hasRealDescription({ summary: candidateSummary, title })) return candidateSummary;
  return localSummary;
};

/**
 * Decide whether the detail page should live-scrape to backfill missing data.
 * Fires when core CONTENT is missing (always), OR when a real description is
 * missing and we haven't scraped recently. The 24h staleness throttle heals
 * legacy recipes (scraped for content by older code that never persisted a real
 * `summary`, or imported with `description = title`) exactly once, then closes
 * because a successful scrape writes both `summary` and a fresh `lastScrapedAt`.
 * Content-complete recipes that already HAVE a real summary never scrape,
 * preserving the #381/#391/#392 storm fix.
 *
 * `lastFailedScrapeAt` closes the one hole that throttle left open. Only a
 * SUCCESSFUL scrape writes `lastScrapedAt`, so a recipe whose site can never be
 * scraped (Le Devoir publishes recipes as `ReportageNewsArticle`, with no
 * machine-readable recipe on the page) stayed `neverScraped` forever and re-ran
 * the full scrape chain on every single open. Pass the device-local failure
 * stamp from `scrapeBackoff` and a failed heal waits out the same 24h a
 * successful one does.
 *
 * Note it gates the HEALS only. `missingContent` is deliberately left ungated:
 * that is a dead end the user can actually see, so it stays worth retrying on
 * every open in case the site recovered.
 */
export const shouldScrapeRecipe = (
  recipe: {
    extendedIngredients?: Array<{ original?: string; name?: string }>;
    instructions?: unknown[];
    summary?: string;
    title?: string;
    sourceUrl?: string;
    videoUrl?: string;
    lastScrapedAt?: string;
  } | null | undefined,
  lastFailedScrapeAt?: string,
): boolean => {
  if (!recipe?.sourceUrl) return false;
  if (recipe.videoUrl || isVideoUrl(recipe.sourceUrl)) return false;

  const neverScraped = !recipe.lastScrapedAt;
  const staleEnoughToRetry = !!recipe.lastScrapedAt &&
    Date.now() - new Date(recipe.lastScrapedAt).getTime() > 24 * 60 * 60 * 1000;
  const failedAt = lastFailedScrapeAt ? new Date(lastFailedScrapeAt).getTime() : NaN;
  const recentlyFailed = Number.isFinite(failedAt) &&
    Date.now() - failedAt < 24 * 60 * 60 * 1000;

  // Every cosmetic heal shares one gate: due for a retry, and not still serving
  // out the backoff from a scrape that already came back empty.
  const healAllowed = (neverScraped || staleEnoughToRetry) && !recentlyFailed;

  const missingContent = !hasRecipeContent(recipe);
  const missingSummaryHeal = !hasRealDescription(recipe) && healAllowed;
  // Heal recipes whose ingredients were stored as names-only (no quantities).
  // After the scrape, lastScrapedAt is set — won't re-trigger for 24h.
  const missingQuantitiesHeal =
    hasRecipeContent(recipe) &&
    !hasIngredientQuantities(recipe) &&
    healAllowed;
  // A newsletter click-tracking sourceUrl still resolves today but expires on
  // its own, so opening the recipe is the last reliable chance to record where
  // it actually points. The scrape persists the resolved address and stamps
  // lastScrapedAt, so this fires at most once a day and stops entirely once
  // repaired. See #575.
  const unstableUrlHeal = isUnstableSourceUrl(recipe.sourceUrl) && healAllowed;

  return missingContent || missingSummaryHeal || missingQuantitiesHeal || unstableUrlHeal;
};

/**
 * True when a recipe has all the non-content metadata a card/detail view can
 * show: image, source rating, and cook time. Deliberately separate from
 * `hasRecipeContent` — metadata is cosmetic, not blocking, and must never
 * trigger the live-scrape-on-view flow (only `useRecipeEnrichment`'s
 * background pass should ever chase this).
 */
export const isRecipeMetadataComplete = (recipe: {
  image?: string;
  sourceRating?: number;
  readyInMinutes?: number;
} | null | undefined): boolean => {
  if (!recipe) return false;
  return !!recipe.image && recipe.sourceRating != null && (recipe.readyInMinutes ?? 0) > 0;
};

/**
 * True when a recipe-detail view has SETTLED on genuinely-missing content that
 * is worth alerting on. This is the signal a "missing ingredients" alert should
 * fire on — NOT the mount-time `recipe_detail_opened` snapshot, which is empty
 * by design (a thin meal-plan/route snapshot) before the async background load
 * populates content. Returns false for video recipes (no structured ingredients
 * or steps are expected) and for recipes that actually have content. A null
 * recipe (not found in any source) counts as unavailable.
 */
export const isRecipeContentUnavailable = (recipe: {
  extendedIngredients?: unknown[];
  instructions?: unknown[];
  videoUrl?: string;
  sourceUrl?: string;
} | null | undefined): boolean => {
  if (hasRecipeContent(recipe)) return false;
  if (!recipe) return true; // not found anywhere → genuinely unavailable
  if (recipe.videoUrl) return false;
  if (recipe.sourceUrl && isVideoUrl(recipe.sourceUrl)) return false;
  return true;
};

/**
 * Get the favicon URL for a given source URL using Google's favicon service
 */
export const getSourceFaviconUrl = (url: string): string | null => {
  try {
    const hostname = new URL(url).hostname;
    return `https://www.google.com/s2/favicons?domain=${hostname}&sz=32`;
  } catch {
    return null;
  }
};

/**
 * Extract a readable source name from URL
 * e.g., "cooking.nytimes.com" -> "NYT Cooking"
 *       "www.bonappetit.com" -> "Bon Appetit"
 */
export const getSourceDisplayName = (url: string): string => {
  try {
    const hostname = new URL(url).hostname.toLowerCase();

    // Known source mappings
    const knownSources: Record<string, string> = {
      'cooking.nytimes.com': 'NYT Cooking',
      'www.nytimes.com': 'New York Times',
      'nytimes.com': 'New York Times',
      'www.bonappetit.com': 'Bon Appetit',
      'bonappetit.com': 'Bon Appetit',
      'www.seriouseats.com': 'Serious Eats',
      'seriouseats.com': 'Serious Eats',
      'www.epicurious.com': 'Epicurious',
      'epicurious.com': 'Epicurious',
      'www.foodnetwork.com': 'Food Network',
      'foodnetwork.com': 'Food Network',
      'www.allrecipes.com': 'AllRecipes',
      'allrecipes.com': 'AllRecipes',
      'www.delish.com': 'Delish',
      'delish.com': 'Delish',
      'www.tasty.co': 'Tasty',
      'tasty.co': 'Tasty',
      'www.food52.com': 'Food52',
      'food52.com': 'Food52',
      'www.thekitchn.com': 'The Kitchn',
      'thekitchn.com': 'The Kitchn',
      'www.simplyrecipes.com': 'Simply Recipes',
      'simplyrecipes.com': 'Simply Recipes',
      'www.budgetbytes.com': 'Budget Bytes',
      'budgetbytes.com': 'Budget Bytes',
      'www.ricardocuisine.com': 'Ricardo Cuisine',
      'ricardocuisine.com': 'Ricardo Cuisine',
      'www.kpourkatrine.com': 'K pour Katrine',
      'kpourkatrine.com': 'K pour Katrine',
      'www.troisfoisparjour.com': 'Trois fois par jour',
      'troisfoisparjour.com': 'Trois fois par jour',
      // Without this the hostname fallback renders "Coupdepouce".
      'www.coupdepouce.com': 'Coup de Pouce',
      'coupdepouce.com': 'Coup de Pouce',
    };

    if (knownSources[hostname]) {
      return knownSources[hostname];
    }

    // Fallback: Clean up hostname
    // Remove www. and common TLDs, capitalize
    let name = hostname
      .replace(/^www\./, '')
      .replace(/\.(com|org|net|co|io)$/, '')
      .split('.')[0];

    // Capitalize first letter
    return name.charAt(0).toUpperCase() + name.slice(1);
  } catch {
    return 'Original';
  }
};
